import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import { ProjectKind, VAppProjectEntityKind } from '@prophecy/graphql-types/enums';
import { type VAppProject } from '@prophecy/queries/common/VerticalApps/projectListQuery';
import { SQL_PROVIDER } from 'frontend/core/src/constants/sql';

export type ProjectListingEntityKind = `${VAppProjectEntityKind}`;

// The two provider values a VApp project's `providerType` can resolve to today.
export type ProjectListingProvider = SQL_PROVIDER.DataBricks | ORCHESTRATION_PROVIDER.ProphecyManaged;

export type ProjectListingFilters = {
  kinds: ProjectListingEntityKind[];
  providers: ProjectListingProvider[];
};

export const EMPTY_PROJECT_LISTING_FILTERS: ProjectListingFilters = {
  kinds: [],
  providers: []
};

export type ProjectListingFacetOption<T extends string> = {
  id: T;
  label: string;
  count: number;
};

export type ProjectListingFacets = {
  kindOptions: ProjectListingFacetOption<ProjectListingEntityKind>[];
  providerOptions: ProjectListingFacetOption<ProjectListingProvider>[];
};

// Labels/order match the Figma filter list.
const ENTITY_KIND_LABELS: Record<ProjectListingEntityKind, string> = {
  [VAppProjectEntityKind.CDM]: 'CDM',
  [VAppProjectEntityKind.Mapping]: 'Data Mapping',
  [VAppProjectEntityKind.Pipeline]: 'Pipeline',
  [VAppProjectEntityKind.Analysis]: 'Analysis'
};

const ENTITY_KIND_ORDER: ProjectListingEntityKind[] = [
  VAppProjectEntityKind.CDM,
  VAppProjectEntityKind.Mapping,
  VAppProjectEntityKind.Pipeline,
  VAppProjectEntityKind.Analysis
];

const PROVIDER_LABELS: Record<ProjectListingProvider, string> = {
  [SQL_PROVIDER.DataBricks]: 'Databricks',
  [ORCHESTRATION_PROVIDER.ProphecyManaged]: 'Prophecy Managed'
};

const PROVIDER_ORDER: ProjectListingProvider[] = [SQL_PROVIDER.DataBricks, ORCHESTRATION_PROVIDER.ProphecyManaged];

export function getProjectListingEntityKindLabel(kind: ProjectListingEntityKind) {
  return ENTITY_KIND_LABELS[kind];
}

export function getProjectListingProviderLabel(provider: ProjectListingProvider) {
  return PROVIDER_LABELS[provider];
}

export function getProjectListingProvider(providerType: string | undefined): ProjectListingProvider | undefined {
  const normalized = providerType?.toLowerCase();
  return PROVIDER_ORDER.find((provider) => provider.toLowerCase() === normalized);
}

/** A Types project holds only an ontology, so it belongs to the Types Library and nowhere else. */
export function isTypesProject(project: VAppProject): boolean {
  return project.projectKind === ProjectKind.Types;
}

export function hasActiveProjectListingFilters(filters: ProjectListingFilters) {
  return filters.kinds.length > 0 || filters.providers.length > 0;
}

function projectHasEntityKind(project: VAppProject, kind: ProjectListingEntityKind): boolean {
  return (project.entities ?? []).some((entity) => entity.kind === kind && entity.count > 0);
}

export function filterProjectsByListingFilters(projects: VAppProject[], filters: ProjectListingFilters): VAppProject[] {
  if (!hasActiveProjectListingFilters(filters)) {
    return projects;
  }

  return projects.filter((project) => {
    const matchesKind = filters.kinds.length === 0 || filters.kinds.some((kind) => projectHasEntityKind(project, kind));
    const matchesProvider =
      filters.providers.length === 0 || filters.providers.includes(getProjectListingProvider(project.providerType)!);
    return matchesKind && matchesProvider;
  });
}

export function computeProjectListingFacets(projects: VAppProject[]): ProjectListingFacets {
  const kindCounts = new Map<ProjectListingEntityKind, number>();
  const providerCounts = new Map<ProjectListingProvider, number>();

  for (const project of projects) {
    const seenKinds = new Set<ProjectListingEntityKind>();

    for (const entity of project.entities ?? []) {
      const kind = entity.kind as ProjectListingEntityKind;
      if (entity.count > 0 && !seenKinds.has(kind)) {
        seenKinds.add(kind);
        kindCounts.set(kind, (kindCounts.get(kind) ?? 0) + 1);
      }
    }

    const provider = getProjectListingProvider(project.providerType);
    if (provider) {
      providerCounts.set(provider, (providerCounts.get(provider) ?? 0) + 1);
    }
  }

  // Show every known kind/provider so counts of 0 still render, matching the Figma list.
  const kindOptions = ENTITY_KIND_ORDER.map((kind) => ({
    id: kind,
    label: getProjectListingEntityKindLabel(kind),
    count: kindCounts.get(kind) ?? 0
  }));

  const providerOptions = PROVIDER_ORDER.map((provider) => ({
    id: provider,
    label: getProjectListingProviderLabel(provider),
    count: providerCounts.get(provider) ?? 0
  }));

  return { kindOptions, providerOptions };
}

export function toggleProjectListingEntityKind(
  filters: ProjectListingFilters,
  kind: ProjectListingEntityKind
): ProjectListingFilters {
  const isSelected = filters.kinds.includes(kind);

  return {
    ...filters,
    kinds: isSelected ? filters.kinds.filter((selectedKind) => selectedKind !== kind) : [...filters.kinds, kind]
  };
}

export function toggleProjectListingProvider(
  filters: ProjectListingFilters,
  provider: ProjectListingProvider
): ProjectListingFilters {
  const isSelected = filters.providers.includes(provider);

  return {
    ...filters,
    providers: isSelected
      ? filters.providers.filter((selectedProvider) => selectedProvider !== provider)
      : [...filters.providers, provider]
  };
}
